import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useA11ySelector } from '@sg/a11y-helpers';
import { useToggleState } from '@sg/garnish';

import {
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenTitle,
  ActiveBagWarning,
  AddressAddModal,
  AddressEditModal,
  AddressList,
  useActiveBagWarning,
} from '../../components';
import { useCustomer } from '../../context/Customer';

export const AddressesScreen = () => {
  const { formatMessage } = useIntl();
  const { customer } = useCustomer();
  const { addresses } = customer;

  const containerA11ySelector = useA11ySelector('account.addresses.container');

  // ─── State ───────────────────────────────────────────────────────────

  const [selectedAddressId, selectAddressId] = useState('');

  // ─── Toggles ─────────────────────────────────────────────────────────

  const {
    value: isShowingAddAddressModal,
    toggleOn: showAddAddressModal,
    toggleOff: hideAddAddress,
  } = useToggleState();

  const showEditAddressModal = useCallback((addressId: string) => {
    selectAddressId(addressId);
  }, []);

  const hideEditAddressModal = useCallback(() => {
    selectAddressId('');
  }, []);

  const { activeBagWarning, closeActiveBagWarning, confirmEditAddress, onEdit } =
    useActiveBagWarning(showEditAddressModal);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer screen="addresses" {...containerA11ySelector.props}>
      <AccountScreenTitle title={formatMessage(messages.addressesTitle)} />

      <AccountScreenSection>
        <AddressList
          items={addresses ?? []}
          onSelectItem={onEdit}
          onSelectAdd={showAddAddressModal}
        />
      </AccountScreenSection>

      <ActiveBagWarning
        visible={Boolean(activeBagWarning)}
        onClose={closeActiveBagWarning}
        onConfirm={confirmEditAddress}
      />

      <AddressAddModal visible={isShowingAddAddressModal} onRequestClose={hideAddAddress} />

      <AddressEditModal
        addressId={selectedAddressId}
        visible={Boolean(selectedAddressId)}
        onRequestClose={hideEditAddressModal}
      />
    </AccountScreenContainer>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addressesTitle: {
    defaultMessage: 'Addresses',
    description: 'Addresses | Title',
  },
});
